import { useEffect, useRef, useState } from 'react';
import 'maplibre-gl/dist/maplibre-gl.css';
import '@dale/map/map.css';
import { createPinElement, daleMapStyle, DALE_MAP_REGIONS, registerDaleMaps } from '@dale/map';
import type { Map as MapLibreMap, Marker } from 'maplibre-gl';

import { isZonedCountry, mapRegionOf } from '../lib/country';
import { ZONE_CENTROIDS } from '../lib/zone-centroids';
import { reverseAddress, searchAddress, type AddressHit } from '../lib/place-search';
import { WORLD_AREA_ZOOM, WORLD_MAP_STYLE } from '../lib/world-map';

const DEFAULT_ZOOM = 15;
const PIN_ZOOM = 17;
const SEARCH_DEBOUNCE_MS = 400;
const MIN_QUERY_LENGTH = 3;
/** How far past the country's data the camera may drift, in degrees. */
const BOUNDS_MARGIN = 0.5;

const SEARCH_FAILED = 'No pudimos buscar la dirección. Toca el mapa para marcarla.';
const NO_MAP = 'Este dispositivo no puede mostrar el mapa. Busca la dirección arriba.';

type MapPickerProps = {
  lat: number | null;
  lng: number | null;
  neighborhood: string;
  onChange: (lat: number, lng: number) => void;
  onClear: () => void;
  /** Fired with a human address whenever a search hit or a reverse lookup resolves one. */
  onAddress?: (address: string) => void;
  /**
   * ISO code of the business's country. Outside Panamá and Venezuela (2 Nov 2026) the picker opens a world map
   * (`WORLD_MAP_STYLE`) with no fence and moves to the area in `neighborhood`. Absent: the country of the zone.
   */
  country?: string;
};

/**
 * Where the business is, on Dale's own map (OpenStreetMap in Dale's colours):
 * type the address or the place name and pick a hit, tap the map, drag the
 * pin, or use the phone's location. Any of the four moves the pin; the
 * address field fills itself from whatever was picked.
 */
export function MapPicker({ lat, lng, neighborhood, onChange, onClear, onAddress, country: businessCountry }: MapPickerProps) {
  const hasPin = lat !== null && lng !== null;
  // Panamá and Venezuela open Dale's own map of the country; anywhere else the world map.
  const zonedCountry =
    businessCountry === undefined ? mapRegionOf(neighborhood) : isZonedCountry(businessCountry) ? businessCountry : undefined;
  const country = zonedCountry ?? 'PA';
  const searchCountry = zonedCountry ?? businessCountry ?? 'PA';
  const abroad = zonedCountry === undefined;
  const fallbackCentroid = ZONE_CENTROIDS['Casco Viejo'] as readonly [number, number];
  const zoneCentroid = ZONE_CENTROIDS[neighborhood] ?? fallbackCentroid;

  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<AddressHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | undefined>();
  const [locating, setLocating] = useState(false);
  const [mapFailed, setMapFailed] = useState(false);
  const requestId = useRef(0);
  // The label of the hit just picked: typing it back into the box is not a new search.
  const pickedLabel = useRef<string | null>(null);
  const container = useRef<HTMLDivElement>(null);
  const map = useRef<MapLibreMap | null>(null);
  const marker = useRef<Marker | null>(null);
  // The map's listeners outlive renders; they reach the latest props through here.
  const pickRef = useRef<(nextLat: number, nextLng: number) => void>(() => undefined);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH || trimmed === pickedLabel.current) {
      setHits([]);
      setSearching(false);
      return;
    }
    const id = ++requestId.current;
    setSearching(true);
    setSearchError(undefined);
    const timer = setTimeout(() => {
      searchAddress(trimmed, abroad ? undefined : zoneCentroid, searchCountry)
        .then((results) => {
          if (id !== requestId.current) return;
          setHits(results);
          setSearching(false);
        })
        .catch(() => {
          if (id !== requestId.current) return;
          setSearchError(SEARCH_FAILED);
          setSearching(false);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, zoneCentroid, abroad, searchCountry]);

  function pickPoint(nextLat: number, nextLng: number) {
    onChange(nextLat, nextLng);
    if (!onAddress) return;
    void reverseAddress(nextLat, nextLng)
      .then((address) => {
        if (address) onAddress(address);
      })
      .catch(() => undefined);
  }
  pickRef.current = pickPoint;

  // One map per country; the pin and the camera follow the props without remounting it.
  useEffect(() => {
    if (!container.current) return;
    const region = DALE_MAP_REGIONS[country];
    const [west, south, east, north] = region.bounds;
    let created: MapLibreMap;
    try {
      const maplibregl = registerDaleMaps();
      created = new maplibregl.Map(
        abroad
          ? {
              // Anywhere else: the world, no fence; the area search below moves the camera.
              container: container.current,
              style: WORLD_MAP_STYLE,
              center: hasPin ? [lng, lat] : [-40, 15],
              zoom: hasPin ? PIN_ZOOM : 2,
              attributionControl: { compact: true },
              dragRotate: false,
              pitchWithRotate: false,
            }
          : {
              container: container.current,
              style: daleMapStyle(country),
              center: [zoneCentroid[1], zoneCentroid[0]],
              zoom: DEFAULT_ZOOM,
              maxBounds: [[west - BOUNDS_MARGIN, south - BOUNDS_MARGIN], [east + BOUNDS_MARGIN, north + BOUNDS_MARGIN]],
              attributionControl: { compact: true },
              dragRotate: false,
              pitchWithRotate: false,
            },
      );
      created.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-right');
      created.on('click', (event) => pickRef.current(event.lngLat.lat, event.lngLat.lng));
    } catch {
      setMapFailed(true);
      return;
    }
    map.current = created;
    setMapFailed(false);
    return () => {
      marker.current?.remove();
      marker.current = null;
      created.remove();
      map.current = null;
    };
    // The centroid only seeds the first view; a new zone in the same country keeps the map.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, abroad]);

  // Abroad with no pin yet: find the area the owner wrote, in their country, and show it.
  useEffect(() => {
    const area = neighborhood.trim();
    if (!abroad || hasPin || !area) return;
    let cancelled = false;
    searchAddress(area, undefined, searchCountry)
      .then(([first]) => {
        if (!cancelled && first) map.current?.easeTo({ center: [first.lng, first.lat], zoom: WORLD_AREA_ZOOM });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // Only when the place changes, not on every pin.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abroad, searchCountry, neighborhood]);

  useEffect(() => {
    const current = map.current;
    if (!current) return;
    if (!hasPin) {
      marker.current?.remove();
      marker.current = null;
      current.easeTo({ center: [zoneCentroid[1], zoneCentroid[0]] });
      return;
    }
    if (!marker.current) {
      const maplibregl = registerDaleMaps();
      const pin = new maplibregl.Marker({ element: createPinElement({ label: 'Ubicación del negocio', selected: true, owner: true }), draggable: true, anchor: 'bottom' });
      pin.on('dragend', () => {
        const position = pin.getLngLat();
        pickRef.current(position.lat, position.lng);
      });
      marker.current = pin.setLngLat([lng, lat]).addTo(current);
    } else {
      marker.current.setLngLat([lng, lat]);
    }
    current.easeTo({ center: [lng, lat], zoom: Math.max(current.getZoom(), PIN_ZOOM) });
  }, [hasPin, lat, lng, zoneCentroid, mapFailed]);

  function pickHit(hit: AddressHit) {
    requestId.current += 1;
    pickedLabel.current = hit.label;
    setHits([]);
    setQuery(hit.label);
    onChange(hit.lat, hit.lng);
    onAddress?.(hit.label);
  }

  function useMyLocation() {
    if (!('geolocation' in navigator)) {
      setSearchError('Este dispositivo no comparte ubicación.');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocating(false);
        pickPoint(position.coords.latitude, position.coords.longitude);
      },
      () => {
        setLocating(false);
        setSearchError('No pudimos leer tu ubicación. Busca la dirección o toca el mapa.');
      },
      { enableHighAccuracy: true, timeout: 8000 },
    );
  }

  return (
    <div className="map-picker">
      <div className="map-search">
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Busca la dirección o el nombre del lugar"
          aria-label="Buscar dirección"
          autoComplete="off"
        />
        <button type="button" className="btn small secondary" onClick={useMyLocation} disabled={locating} aria-label="Usar mi ubicación">
          {locating ? 'Ubicando…' : 'Mi ubicación'}
        </button>
      </div>
      {searching ? <p className="field-help">Buscando…</p> : null}
      {searchError ? (
        <p role="alert" className="field-error">
          {searchError}
        </p>
      ) : null}
      {hits.length > 0 ? (
        <ul className="map-hits" role="listbox" aria-label="Resultados">
          {hits.map((hit) => (
            <li key={hit.id}>
              <button type="button" role="option" aria-selected={false} onClick={() => pickHit(hit)}>
                <span>{hit.name}</span>
                <small>{hit.detail}</small>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {mapFailed ? (
        <p className="field-help map-picker-map map-picker-fallback">{NO_MAP}</p>
      ) : (
        <div ref={container} className="map-picker-map" role="region" aria-label="Mapa para marcar el negocio" />
      )}
      <p className="field-help">
        {hasPin ? 'Arrastra el pin hasta la entrada si quedó corrido.' : 'Toca el mapa en la calle o la entrada de tu negocio.'}
        {hasPin ? (
          <>
            {' '}
            <button type="button" className="linklike" onClick={onClear}>
              Quitar pin
            </button>
          </>
        ) : null}
      </p>
    </div>
  );
}
